import type { CSSProperties } from "react";
import type { ImageFieldValue, ImageHeightOverride, ImageHeightValue, ImageOptions } from "../types";

type ResolvedImage = {
  src: string;
  options?: ImageOptions;
};

/** Style object type used by mergeStyles to avoid csstype version conflicts when the SDK is consumed by apps (e.g. Next) that use a different @types/react/csstype. */
export type StyleObject = Record<string, string | number>;

type ImageOverrideProps = {
  className?: string;
  style?: CSSProperties;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const isValidNumber = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value);

const getEnabledNumber = (value?: ImageHeightValue) =>
  value?.enabled && isValidNumber(value.value) ? (value.value as number) : null;

const hasEffectiveHeight = (override?: ImageHeightOverride | null): boolean =>
  !!(
    override?.enabled &&
    (getEnabledNumber(override.minHeight) != null ||
      getEnabledNumber(override.maxHeight) != null ||
      getEnabledNumber(override.height) != null)
  );

/** Resolves an image field value into separate source and options. */
export const resolveImageValue = (value?: ImageFieldValue | null): ResolvedImage => {
  if (!value) return { src: "" };
  if (typeof value === "string") return { src: value };
  return { src: value.src ?? "", options: value.options };
};

/** Computes the object-position style from the custom image center point. */
export const getImageObjectPositionStyle = (options?: ImageOptions): CSSProperties | undefined => {
  const center = options?.center;
  if (!center?.enabled) return undefined;
  const x = clamp(isValidNumber(center.x) ? (center.x as number) : 0, -1, 1);
  const y = clamp(isValidNumber(center.y) ? (center.y as number) : 0, -1, 1);
  const xPercent = ((x + 1) / 2) * 100;
  const yPercent = ((y + 1) / 2) * 100;
  return { objectPosition: `${xPercent}% ${yPercent}%` };
};

const applyOverride = (
  label: "mobile" | "tablet" | "desktop",
  override: ImageHeightOverride | undefined,
  style: Record<string, string>,
  classes: string[]
) => {
  if (!override?.enabled) return;
  let hasAny = false;
  const minHeightValue = getEnabledNumber(override.minHeight);
  const maxHeightValue = getEnabledNumber(override.maxHeight);
  const heightValue = getEnabledNumber(override.height);

  if (minHeightValue !== null) {
    style[`--image-min-h-${label}`] = `${minHeightValue}px`;
    hasAny = true;
  }
  if (maxHeightValue !== null) {
    style[`--image-max-h-${label}`] = `${maxHeightValue}px`;
    hasAny = true;
  }
  if (heightValue !== null) {
    style[`--image-h-${label}`] = `${heightValue}px`;
    hasAny = true;
  }

  if (hasAny) {
    classes.push(`image-override-${label}`);
  }
};

/**
 * Resolve height overrides with cascade: desktop applies to all viewports;
 * tablet applies to tablet and mobile unless mobile overrides; mobile overrides only mobile.
 */
const resolveHeightCascade = (overrides: NonNullable<ImageOptions["overrides"]>) => {
  const d = overrides.desktop;
  const t = overrides.tablet;
  const m = overrides.mobile;
  return {
    desktop: d,
    tablet: hasEffectiveHeight(t) ? t : d,
    mobile: hasEffectiveHeight(m) ? m : hasEffectiveHeight(t) ? t : d,
  };
};

/** Generates CSS props (classes and styles) for per-breakpoint height overrides. */
export const getImageOverrideProps = (options?: ImageOptions): ImageOverrideProps => {
  const overrides = options?.overrides;
  const style: Record<string, string | number> = {};
  const classes: string[] = [];

  if (options?.fit) {
    style.objectFit = options.fit;
  }

  if (overrides) {
    const resolved = resolveHeightCascade(overrides);
    applyOverride("mobile", resolved.mobile, style as Record<string, string>, classes);
    applyOverride("tablet", resolved.tablet, style as Record<string, string>, classes);
    applyOverride("desktop", resolved.desktop, style as Record<string, string>, classes);
  }

  return {
    className: classes.length > 0 ? classes.join(" ") : undefined,
    style: Object.keys(style).length > 0 ? (style as CSSProperties) : undefined,
  };
};

/** Merges multiple CSS class names into a single string. */
export const mergeClassNames = (...values: Array<string | undefined>) =>
  values.filter(Boolean).join(" ");

/**
 * Merges style objects. Accepts any object so both React.CSSProperties (SDK
 * and consuming app) work regardless of csstype/@types/react version. Returns
 * StyleObject for compatibility with any consumer's style prop.
 */
export const mergeStyles = (...values: Array<object | undefined>): StyleObject | undefined => {
  const merged: Record<string, string | number> = {};
  values.forEach((value) => {
    if (!value || typeof value !== "object") return;
    for (const key of Object.keys(value)) {
      const v = (value as Record<string, unknown>)[key];
      if (typeof v === "string" || typeof v === "number") merged[key] = v;
    }
  });
  return Object.keys(merged).length > 0 ? merged : undefined;
};
